import { Camera, CheckCircle, ShieldCheck, X } from 'lucide-react';
import { useState } from 'react';
import type { PlayerUser } from '../../entities/user/types';
import { useCountryStore } from '../../shared/country/countryStore';
import { marketCopyKey } from '../../shared/country/marketCopy';
import { useI18nStore } from '../../shared/i18n/i18nStore';

type KycFormStatus = 'idle' | 'submitting' | 'approved';

export function isKycVerified(user: PlayerUser | null) {
  return String(user?.kyc_status || '').toLowerCase() === 'verified';
}

export function withVerifiedKyc(user: PlayerUser | null): PlayerUser | null {
  if (!user) return user;
  return { ...user, kyc_status: 'verified' };
}

export function KycRequiredSheet({
  onVerify,
  onClose,
}: {
  onVerify: () => void;
  onClose: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  const countryCode = useCountryStore((state) => state.country.code);
  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="kyc-required-sheet-react" role="dialog" aria-modal="true" aria-label={t('kycRequiredTitle')} onMouseDown={(event) => event.stopPropagation()}>
        <div className="sheet-grabber" />
        <button className="icon-button kyc-close-button" type="button" onClick={onClose} aria-label={t('close')}>
          <X size={18} aria-hidden="true" />
        </button>
        <div className="kyc-required-icon">
          <ShieldCheck size={36} aria-hidden="true" />
        </div>
        <h2>{t('kycRequiredTitle')}</h2>
        <p>{t(marketCopyKey('kycRequiredBody', countryCode))}</p>
        <button className="primary-button sheet-submit" type="button" onClick={onVerify}>
          {t('kycRequiredCta')}
        </button>
        <button className="link-button" type="button" onClick={onClose}>
          {t('kycLater')}
        </button>
      </section>
    </div>
  );
}

export function KycFormSheet({
  user,
  onClose,
  onApproved,
  approvedActionLabel,
}: {
  user: PlayerUser | null;
  onClose: () => void;
  onApproved: (updatedUser: PlayerUser | null) => void;
  approvedActionLabel?: string;
}) {
  const t = useI18nStore((state) => state.t);
  const countryCode = useCountryStore((state) => state.country.code);
  const [cpf, setCpf] = useState('');
  const [birth, setBirth] = useState('');
  const [photo, setPhoto] = useState(false);
  const [status, setStatus] = useState<KycFormStatus>('idle');
  const canSubmit = cpf.replace(/\D/g, '').length === 11 && /^\d{4}-\d{2}-\d{2}$/.test(birth) && photo;

  function submit() {
    if (!canSubmit || status !== 'idle') return;
    setStatus('submitting');
    window.setTimeout(() => {
      setStatus('approved');
      onApproved(withVerifiedKyc(user));
    }, 900);
  }

  return (
    <div className="sheet-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="profile-sheet kyc-form-sheet-react" role="dialog" aria-modal="true" aria-label={t('kycFormTitle')} onMouseDown={(event) => event.stopPropagation()} data-kyc-react="form">
        <div className="sheet-grabber" />
        <header className="sheet-header">
          <div>
            <h2>{t('kycFormTitle')}</h2>
            <p>{t(marketCopyKey('kycRequiredBody', countryCode))}</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label={t('close')}>
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        {status === 'idle' ? (
          <div className="profile-sheet-list">
            <label className="field">
              <span>{t('kycFormCpf')}</span>
              <input
                value={cpf}
                inputMode="numeric"
                maxLength={14}
                placeholder="000.000.000-00"
                onChange={(event) => setCpf(formatCpf(event.target.value))}
              />
            </label>
            <label className="field">
              <span>{t('kycFormBirth')}</span>
              <input
                value={birth}
                type="date"
                onChange={(event) => setBirth(event.target.value)}
              />
            </label>
            <button className={`kyc-photo-drop ${photo ? 'ready' : ''}`} type="button" onClick={() => setPhoto(true)}>
              {photo ? <CheckCircle size={34} aria-hidden="true" /> : <Camera size={34} aria-hidden="true" />}
              <strong>{photo ? t('kycFormPhotoDone') : t('kycFormPhotoUpload')}</strong>
              <span>{t('kycFormPhotoHint')}</span>
            </button>
            <button className="primary-button sheet-submit" type="button" disabled={!canSubmit} onClick={submit}>
              {t('kycFormSubmit')}
            </button>
          </div>
        ) : null}

        {status === 'submitting' ? (
          <div className="kyc-status-panel">
            <span className="spinner" />
            <strong>{t('kycFormSubmitting')}</strong>
          </div>
        ) : null}

        {status === 'approved' ? (
          <div className="kyc-status-panel success">
            <CheckCircle size={54} aria-hidden="true" />
            <strong>{t('kycFormApproved')}</strong>
            <p>{t('kycFormApprovedSub')}</p>
            <button className="primary-button sheet-submit" type="button" onClick={onClose}>
              {approvedActionLabel || t('kycFormBackToWithdraw')}
            </button>
          </div>
        ) : null}
      </section>
    </div>
  );
}

function formatCpf(value: string) {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length > 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
  if (digits.length > 6) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  if (digits.length > 3) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  return digits;
}
